import { Spot } from "../global-commons/type";

const SEARCH_FEEDBACK_ROW_ID = "map-search-feedback-row";

/**
 * 検索語を半角/全角スペースで分割し、空語を除去する。
 */
export function splitSearchKeywords(searchText: string): string[] {
  return searchText
    .split(/[\s\u3000]+/)
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword.length > 0);
}

/**
 * キーワード複数時はAND、各キーワードの判定対象は subject OR description。
 * description はプレーンテキストとして判定する。
 */
export function filterSpotsByKeywords(
  spots: Spot[],
  searchKeyWords?: string[],
): Spot[] {
  const normalizedKeywords = (searchKeyWords ?? [])
    .map((keyword) => keyword.trim().toLowerCase())
    .filter((keyword) => keyword.length > 0);

  if (normalizedKeywords.length === 0) {
    return spots;
  }

  return spots.filter((spot) => {
    const subject = spot.subject.toLowerCase();
    const description = spot.description.toLowerCase();
    return normalizedKeywords.every(
      (keyword) => subject.includes(keyword) || description.includes(keyword),
    );
  });
}

export function upsertSearchNoResultsMessage(
  spotsCount: number,
  searchText: string,
  noResultsText: string,
  messageId: string,
): void {
  // 空白のみ入力は「検索語なし」として扱う。
  const hasKeyword = splitSearchKeywords(searchText).length > 0;
  const shouldShow = hasKeyword && spotsCount === 0;

  const messageRowElem = document.getElementById(SEARCH_FEEDBACK_ROW_ID);
  const messageElem = document.getElementById(messageId);
  if (!messageRowElem || !messageElem) {
    throw new Error("search no-results element not found");
  }

  messageElem.innerText = noResultsText;
  messageRowElem.style.display = shouldShow ? "block" : "none";
  messageElem.style.display = shouldShow ? "flex" : "none";
}

export type RegisterSearchHandlersOptions = {
  delayedSearchMilliseconds: number;
  searchBox: HTMLInputElement;
  searchClearButton: HTMLButtonElement;
  onSearch: () => Promise<void>;
};

export function registerSearchHandlers(
  options: RegisterSearchHandlersOptions,
): void {
  const { delayedSearchMilliseconds, searchBox, searchClearButton, onSearch } =
    options;

  let delayedSearchTimerId: number | null = null;
  let isSearchComposing = false;
  let isSearchRunning = false;
  let needsSearchRerun = false;

  const syncSearchClearButtonVisibility = () => {
    const hasInput = searchBox.value.length > 0;
    searchClearButton.classList.toggle("visible", hasInput);
  };

  const runSearchInOrder = async () => {
    if (isSearchRunning) {
      // 実行中に新しい入力が来た場合、完了後に最新状態でもう一度だけ再検索する。
      needsSearchRerun = true;
      return;
    }

    isSearchRunning = true;
    try {
      do {
        needsSearchRerun = false;
        await onSearch();
      } while (needsSearchRerun);
    } finally {
      isSearchRunning = false;
    }
  };

  const triggerDelayedSearch = () => {
    // 最新入力のみ有効にするため、前回タイマーを必ずキャンセルする。
    if (delayedSearchTimerId !== null) {
      clearTimeout(delayedSearchTimerId);
    }
    delayedSearchTimerId = window.setTimeout(() => {
      // 発火済みタイマーIDは await 前に無効化し、後続タイマーIDの上書きを防ぐ。
      delayedSearchTimerId = null;
      void runSearchInOrder();
    }, delayedSearchMilliseconds);
  };

  syncSearchClearButtonVisibility();
  // IME変換中は中間文字列で検索を走らせない。
  searchBox.addEventListener("compositionstart", () => {
    isSearchComposing = true;
  });
  searchBox.addEventListener("compositionend", () => {
    isSearchComposing = false;
    syncSearchClearButtonVisibility();
    triggerDelayedSearch();
  });
  searchBox.addEventListener("input", (e: Event) => {
    syncSearchClearButtonVisibility();
    const inputEvent = e as InputEvent;
    // 変換確定前の input は無視し、compositionend 後に1回だけ検索する。
    if (isSearchComposing || inputEvent.isComposing) {
      return;
    }
    triggerDelayedSearch();
  });
  searchClearButton.addEventListener("click", () => {
    searchBox.value = "";
    syncSearchClearButtonVisibility();
    triggerDelayedSearch();
    searchBox.focus();
  });
}
